"use client";

import { FC, useEffect, useRef } from "react";
import { useState } from "react";
import { getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
import SkipToMainBtn from "../SkipToMainBtn";
import { GlobalNavigationProps } from "../GlobalNavigation";
import LangDropdown from "./LangDropdown";
import { Menu, XMarkThin } from "@/icons";
import MobileNavigationPanel from "./MobileNavigationPanel";
import { useIsPageOnTop } from "@/hooks";

const MobileNavigation: FC<GlobalNavigationProps> = ({
  sticky,
  menu,
  superHeader,
  locale = "en-us",
  asPath = "/",
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [openNavTop, setOpenNavTop] = useState<number>(0);
  const navRef = useRef<HTMLElement | null>(null);
  const navMenuRef = useRef<HTMLDivElement | null>(null);
  const isPageOnTop = useIsPageOnTop(sticky);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const next = !prev;

      if (next && navRef.current && navMenuRef.current) {
        const navTop = Math.max(navRef.current.getBoundingClientRect().top, 0);
        setOpenNavTop(navTop);
        setOffset(navTop + navMenuRef.current.offsetHeight);
      }

      if (!next) {
        setOpenNavTop(0);
      }

      return next;
    });
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (navRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (navRef.current && navMenuRef.current) {
          const rect = navRef.current.getBoundingClientRect();
          setOffset(rect.top + navMenuRef.current.offsetHeight);
        }
      });
      resizeObserver.observe(navRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [navRef]);

  return (
    <nav
      ref={navRef}
      className={cn(
        "flex flex-col bg-white",
        isMenuOpen && "fixed inset-x-0 bottom-0",
        sticky && !isPageOnTop && !isMenuOpen && "rounded-xl shadow-xs/30",
      )}
      style={isMenuOpen ? { top: `${openNavTop}px` } : undefined}
    >
      <div
        ref={navMenuRef}
        className={cn(
          "relative z-30 flex h-19 items-center justify-between px-6 py-4",
          isMenuOpen && "border-swdc-gray-200 border-b border-solid",
        )}
      >
        {isMenuOpen ? menu?.mobileLogoExpanded : menu?.logo}
        <div className="flex flex-wrap">
          <SkipToMainBtn
            text={superHeader.skipToMainContentLabel}
            classNames="whitespace-pre-line md:whitespace-normal"
          />
        </div>
        <div className="flex items-center gap-4.5 pr-px">
          {isMenuOpen && menu?.cta}
          {superHeader?.languageSwitcher?.languages &&
            superHeader?.languageSwitcher?.languages?.length > 0 &&
            isMenuOpen && (
              <LangDropdown
                languageSwitcher={superHeader.languageSwitcher}
                closeMenu={closeMenu}
                topOffset={offset}
                locale={locale}
                asPath={asPath}
              />
            )}
          {!isMenuOpen && <div className="hidden sm:block">{menu.cta}</div>}
          <button
            className="cursor-pointer text-2xl"
            aria-label={
              isMenuOpen ? "Collapse Mobile Menu" : "Expand Mobile Menu"
            }
            onClick={toggleMenu}
            {...getDataLinkAttributes(
              isMenuOpen
                ? {
                    dld: menu.mobileHamburgerMenuExpandedDLD,
                    dlt: menu.mobileHamburgerMenuExpandedDLT,
                  }
                : {
                    dld: menu.mobileHamburgerMenuCollapsedDLD,
                    dlt: menu.mobileHamburgerMenuCollapsedDLT,
                  },
            )}
          >
            {isMenuOpen ? <XMarkThin width={27} height={27} /> : <Menu />}
          </button>
        </div>
      </div>
      <div className={cn(!isMenuOpen && "hidden", "overflow-y-auto bg-white")}>
        <MobileNavigationPanel
          superHeader={superHeader}
          menu={menu}
          isMenuOpen={isMenuOpen}
          closeMenu={closeMenu}
        />
      </div>
    </nav>
  );
};

export default MobileNavigation;
